import { getSupabaseClient } from '../../lib/supabase'
import { acceptedFileTypes, type DocumentCategory, type DocumentRecord, type DocumentVersion, type DocumentWorkspace } from './document-types'

const bucket = 'property-evidence'

export async function getDocumentWorkspace(propertyId: string): Promise<DocumentWorkspace> {
  const supabase = getSupabaseClient()
  const [documentsResult, assessmentResult, questionsResult] = await Promise.all([
    supabase.from('documents').select('*').eq('property_id', propertyId).order('updated_at', { ascending: false }),
    supabase.from('readiness_assessments').select('id').eq('property_id', propertyId).maybeSingle(),
    supabase.from('readiness_questions').select('id, prompt').order('display_order'),
  ])
  if (documentsResult.error) throw documentsResult.error
  if (assessmentResult.error) throw assessmentResult.error
  if (questionsResult.error) throw questionsResult.error
  const documentIds = documentsResult.data.map((document) => document.id)
  const versionsResult = documentIds.length
    ? await supabase.from('document_versions').select('*').in('document_id', documentIds).order('version_number', { ascending: false })
    : { data: [], error: null }
  if (versionsResult.error) throw versionsResult.error
  return { documents: documentsResult.data, versions: versionsResult.data, assessmentId: assessmentResult.data?.id ?? null, questions: questionsResult.data }
}

function validateFile(file: File) {
  if (!acceptedFileTypes.includes(file.type)) throw new Error('Unsupported file type. Use PDF, Word, Excel, JPEG, PNG or WebP.')
  if (file.size <= 0 || file.size > 10 * 1024 * 1024) throw new Error('File must be smaller than 10 MB.')
}

function safeFileName(name: string) {
  return name.normalize('NFKD').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-').toLowerCase()
}

async function uploadVersion(document: DocumentRecord, file: File): Promise<DocumentVersion> {
  validateFile(file)
  const supabase = getSupabaseClient()
  const path = `${document.organization_id}/${document.property_id}/${document.id}/${crypto.randomUUID()}-${safeFileName(file.name)}`
  const { error: uploadError } = await supabase.storage.from(bucket).upload(path, file, { contentType: file.type, upsert: false })
  if (uploadError) throw uploadError
  const registerResult = await supabase.rpc('register_document_version', {
    target_document_id: document.id,
    target_storage_path: path,
    target_file_name: file.name,
    target_mime_type: file.type,
    target_size_bytes: file.size,
  }) as unknown as { data: unknown; error: { message: string } | null }
  if (registerResult.error) {
    await supabase.storage.from(bucket).remove([path])
    throw new Error(registerResult.error.message)
  }
  const version: unknown = Array.isArray(registerResult.data)
    ? (registerResult.data as unknown[])[0]
    : registerResult.data
  if (!version) {
    await supabase.storage.from(bucket).remove([path])
    throw new Error('The document version could not be registered.')
  }
  return version as DocumentVersion
}

export async function createDocument(input: {
  organizationId: string; propertyId: string; assessmentId: string | null; questionId: string | null
  title: string; category: DocumentCategory; expiresOn: string; userId: string; file: File
}): Promise<{ document: DocumentRecord; version: DocumentVersion }> {
  const supabase = getSupabaseClient()
  validateFile(input.file)
  const { data: document, error } = await supabase.from('documents').insert({
    organization_id: input.organizationId, property_id: input.propertyId,
    assessment_id: input.questionId ? input.assessmentId : null, question_id: input.questionId,
    title: input.title.trim(), category: input.category, expires_on: input.expiresOn || null, created_by: input.userId,
  }).select('*').single()
  if (error) throw error
  try {
    const version = await uploadVersion(document, input.file)
    return { document, version }
  }
  catch (uploadError) { await supabase.from('documents').delete().eq('id', document.id); throw uploadError }
}

export async function deleteCreatedDocument(documentId: string, storagePath: string): Promise<void> {
  const supabase = getSupabaseClient()
  await supabase.storage.from(bucket).remove([storagePath])
  const { error } = await supabase.from('documents').delete().eq('id', documentId)
  if (error) throw error
}

export async function replaceDocument(document: DocumentRecord, file: File): Promise<void> {
  await uploadVersion(document, file)
}

export async function setDocumentArchived(documentId: string, archived: boolean): Promise<void> {
  const { error } = await getSupabaseClient().from('documents').update({ status: archived ? 'archived' : 'active' }).eq('id', documentId)
  if (error) throw error
}

export async function openDocument(storagePath: string): Promise<void> {
  const { data, error } = await getSupabaseClient().storage.from(bucket).createSignedUrl(storagePath, 60)
  if (error) throw error
  window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
}
